import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create UsersContext
const UsersContext = createContext();

// Custom hook to use UsersContext
export const useUsers = () => useContext(UsersContext);

// UsersProvider component
export const UsersProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const API_URL = "https://hrmspayroll.prepstripe.com/api/";
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allUsersWithEmployeeIds, setAllUsersWithEmployeeIds] = useState([]);

  // Set up Axios instance with default headers
  const axiosInstance = useMemo(
    () =>
      axios.create({
        baseURL: API_URL,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
    [API_URL, token] // Recreate the instance if API_URL or token changes
  );

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`users?page=${page}`);
      await new Promise((r) => setTimeout(r, 1000));
      setUsers(response.data.data.data); // Use the 'data' array from the response
      // setUsers([...(response.data.data.data || [])].reverse());
      setPagination({
        currentPage: response.data?.data?.current_page || 1,
        totalPages: response.data?.data?.last_page || 1,
        totalItems: response.data?.data?.total || 0,
        perPage: response.data?.data?.per_page || 10,
      });
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users from the API
  useEffect(() => {
    fetchUsers();
  }, [axiosInstance]);

  const addUser = async (user) => {
    try {
      const response = await axiosInstance.post("users", user);
      setUsers((prevUsers) => [response.data?.data, ...prevUsers]);
      return { success: true };
    } catch (error) {
      console.error("Error adding user:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to add user.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Update existing user
  const updateUser = async (updatedUser) => {
    try {
      const response = await axiosInstance.put(`users/${updatedUser.id}`, updatedUser);
      const updatedUserData = response.data.data; // Extract updated user from API response
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === updatedUserData.id ? { ...user, ...updatedUserData } : user
        )
      );
      return { success: true };
    } catch (error) {
      console.error("Error updating user:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to add user.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Delete user
  const deleteUser = async (id) => {
    try {
      await axiosInstance.delete(`users/${id}`);
      setUsers((prevUsers) => prevUsers.filter((user) => user.id !== id));
      return { success: true };
    } catch (error) {
      // console.error("Error deleting user:", error);
      console.error("Error deleting user:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to delete user.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Fetch all users with employee IDs (no pagination)
  const fetchAllUsersWithEmployeeIds = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('users-with-employee-ids');
      setAllUsersWithEmployeeIds(response.data || []);
    } catch (error) {
      console.error('Error fetching all users with employee ids:', error);
    } finally {
      setLoading(false);
    }
  };

  // console.log("loading in context: ", loading);

  // Memoize the context value
  const value = useMemo(
    () => ({
      pagination,
      loading,
      users,
      error,
      fetchUsers,
      addUser,
      updateUser,
      deleteUser,
      allUsersWithEmployeeIds,
      fetchAllUsersWithEmployeeIds,
    }),
    [users, loading, allUsersWithEmployeeIds] // Recalculate when users or allUsersWithEmployeeIds changes
  );

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
};
